import { useEffect, useMemo, useState } from "react";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";

ChartJS.register(CategoryScale, Filler, Legend, LinearScale, LineElement, PointElement, Tooltip);

const pad = (value) => String(value).padStart(2, "0");
const dateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const daysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return dateKey(date);
};
const number = (value) => Number(value || 0).toLocaleString("en-US");
const reportColors = {
  inventory: "#2385db",
  sales: "#27ae30",
  salesAmount: "#0f4c81",
  inquiries: "#f8c10b",
  appointments: "#7c3aed",
};
const modelChartColors = ["#2385db", "#27ae30", "#f8c10b", "#7c3aed", "#ef4444"];
const reportLabels = {
  inventory: "Inventory added",
  sales: "Sales",
  salesAmount: "Total amount sold",
  inquiries: "Inquiries",
  appointments: "Appointments",
};
const summaryCards = (summary = {}) => [
  ["ti-car", "Total inventory", summary.totalInventory, "All vehicle records"],
  ["ti-rosette-discount-check", "Sold vehicles", summary.soldVehicles, "Closed inventory"],
  ["ti-message-circle", "Inquiries", summary.customerInquiries, "Customer leads"],
  ["ti-calendar-clock", "Appointments", summary.appointments, "Scheduled visits"],
];
const seriesFor = (reports, key) => (reports?.series || []).find((series) => series.key === key) || {
  key,
  label: reportLabels[key] || "Report",
  points: [],
};
const formatDateLabel = (value) => {
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString([], { month: "short", day: "numeric" });
};
const currency = (value) => Number(value || 0).toLocaleString("en-US", {
  currency: "USD",
  maximumFractionDigits: 0,
  style: "currency",
});
const chartDataFor = (series) => {
  const color = reportColors[series.key] || "#2385db";
  return {
    labels: (series.points || []).map((point) => formatDateLabel(point.date)),
    datasets: [
      {
        label: series.label,
        data: (series.points || []).map((point) => Number(point.value || 0)),
        borderColor: color,
        backgroundColor: `${color}22`,
        borderWidth: 2,
        pointRadius: 2,
        tension: 0.35,
        fill: true,
      },
    ],
  };
};
const chartDataForSeries = (seriesList = []) => {
  const labels = (seriesList[0]?.points || []).map((point) => formatDateLabel(point.date));
  return {
    labels,
    datasets: seriesList.map((series, index) => {
      const color = modelChartColors[index % modelChartColors.length];
      return {
        label: series.label,
        data: (series.points || []).map((point) => Number(point.value || 0)),
        borderColor: color,
        backgroundColor: `${color}20`,
        borderWidth: 2,
        pointRadius: 2,
        tension: 0.35,
        fill: false,
      };
    }),
  };
};
const chartOptions = (options = {}) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: Boolean(options.showLegend), position: "bottom" },
    tooltip: {
      intersect: false,
      mode: "index",
      callbacks: options.currency
        ? { label: (context) => `${context.dataset.label}: ${currency(context.parsed.y)}` }
        : undefined,
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { maxTicksLimit: 6 } },
    y: {
      beginAtZero: true,
      ticks: {
        callback: options.currency ? (value) => currency(value) : undefined,
        precision: 0,
      },
    },
  },
});

export default function ReportsPage() {
  const { user } = useAuth();
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const [startDate, setStartDate] = useState(() => daysAgo(29));
  const [endDate, setEndDate] = useState(() => dateKey(new Date()));
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reportSeries = useMemo(
    () => ["inventory", "sales", "inquiries", "appointments"].map((key) => seriesFor(reports, key)),
    [reports],
  );
  const salesAmountSeries = useMemo(() => seriesFor(reports, "salesAmount"), [reports]);
  const topSoldModelSeries = useMemo(
    () => (reports?.series || []).filter((series) => String(series.key || "").startsWith("topSoldModel:")),
    [reports],
  );

  const loadReports = async () => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await careService.reports.overview({
        wid: selectedWorkspaceId,
        workspaceId: selectedWorkspaceId,
        startDate,
        endDate,
      }, actorId);
      setReports(data);
    } catch (nextError) {
      setReports(null);
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to load reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actorId, endDate, selectedWorkspaceId, startDate, workspaceListReady]);

  return (
    <CareShell className="dashboard-content vehicles-page reports-page">
      <section className="reports-page-head">
        <div className="page-title">
          <span className="section-eyebrow">Reports</span>
          <h1>Reports</h1>
          <p>Review inventory, sales, inquiries, and appointment activity over time.</p>
        </div>
        <div className="reports-range">
          <label>
            Start
            <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
          </label>
          <label>
            End
            <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
          </label>
          <button className="btn primary" type="button" onClick={loadReports} disabled={loading}>
            <i className="ti ti-refresh" /> {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </section>

      {error ? <p className="form-error">{error}</p> : null}

      <div className="reports-summary-grid">
        {summaryCards(reports?.summary).map(([icon, label, value, note]) => (
          <article className="reports-summary-card" key={label}>
            <span className="tiny-icon"><i className={`ti ${icon}`} /></span>
            <div>
              <strong>{label}</strong>
              <b>{loading ? "..." : number(value)}</b>
              <small>{note}</small>
            </div>
          </article>
        ))}
      </div>

      <div className="reports-chart-grid">
        {reportSeries.map((series) => (
          <Panel title={series.label || "Report"} key={series.key || series.label}>
            <div className="reports-chart-card">
              {loading ? (
                <div className="reports-chart-empty">
                  <i className="ti ti-loader-2" />
                  <span>Loading report data...</span>
                </div>
              ) : series.points?.length ? (
                <Line data={chartDataFor(series)} options={chartOptions()} />
              ) : (
                <div className="reports-chart-empty">
                  <i className="ti ti-chart-line" />
                  <span>No report data for this range.</span>
                </div>
              )}
            </div>
          </Panel>
        ))}
        <Panel title="Top models sold">
          <div className="reports-chart-card">
            {loading ? (
              <div className="reports-chart-empty">
                <i className="ti ti-loader-2" />
                <span>Loading report data...</span>
              </div>
            ) : topSoldModelSeries.length ? (
              <Line data={chartDataForSeries(topSoldModelSeries)} options={chartOptions({ showLegend: true })} />
            ) : (
              <div className="reports-chart-empty">
                <i className="ti ti-chart-line" />
                <span>No sold model data for this range.</span>
              </div>
            )}
          </div>
        </Panel>
        <Panel title="Total amount sold">
          <div className="reports-chart-card">
            {loading ? (
              <div className="reports-chart-empty">
                <i className="ti ti-loader-2" />
                <span>Loading report data...</span>
              </div>
            ) : salesAmountSeries.points?.length ? (
              <Line data={chartDataFor(salesAmountSeries)} options={chartOptions({ currency: true })} />
            ) : (
              <div className="reports-chart-empty">
                <i className="ti ti-chart-line" />
                <span>No sales amount data for this range.</span>
              </div>
            )}
          </div>
        </Panel>
      </div>
    </CareShell>
  );
}
